// apps/admin/src/pages/whitelist/WhitelistPage.tsx
//
// Hub for per-merchant capabilities that are CLOSED by default and opened
// one merchant at a time.
//
// Why a hub rather than a card on an existing settings page: these switches
// are per-MERCHANT, while `GatewaySettingsPage` is the platform-global
// surface (decision P4.1.D-21, whose Amendment #8 explicitly rejected
// "N checkboxes per merchant" there). And until this page existed there was
// no way to answer "who is whitelisted?" at all — the only surface was a
// toggle buried in each merchant's detail tab, so an operator could not audit
// the list or resume an interrupted rollout.
//
// Adding a capability later = one more `TabKey` and one more panel. Empty
// placeholder tabs are deliberately NOT pre-created: a tab that renders
// nothing reads as a broken page, not as a promise.

import type { JSX } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import { useAuthStore, useHasPermission } from '../../stores/useAuthStore.js';
import { PERMISSIONS } from '../../lib/permissions.js';

import { WhitelistMerchantsTab } from './tabs/WhitelistMerchantsTab.js';
import { WhitelistPaymentMethodsTab } from './tabs/WhitelistPaymentMethodsTab.js';
import { WhitelistConfigTab } from './tabs/WhitelistConfigTab.js';
import { WhitelistLogTab } from './tabs/WhitelistLogTab.js';

type TabKey = 'merchants' | 'paymentMethods' | 'config' | 'log';

/**
 * Getting through the door at all.
 *
 * This is a billing-capability hub, so reaching it is itself the privilege —
 * the Config tab explains WHICH merchants may be charged by card and under
 * what rules, which is not public information just because rendering it costs
 * no API call. Gating only the API-backed tabs left the page open to every
 * logged-in admin: `config` needed no permission, so `allowed` was never
 * empty and the refusal below was unreachable code.
 */
const PAGE_PERMISSION = PERMISSIONS.BILLING_FRAUD_CONFIG;

/**
 * Each tab reads a DIFFERENT endpoint, so each needs its own permission ON
 * TOP of the page gate.
 *
 * Gating the whole page on one of them would still leave an operator staring
 * at 403s in the others — the Payment-methods tab reads platform gateway
 * settings, and the Log tab reads the audit log, neither of which is implied
 * by the card-payment capability. `null` means the tab reads nothing a
 * permission guards beyond `PAGE_PERMISSION` (Config is static copy).
 */
const TAB_PERMISSION: Record<TabKey, string | null> = {
  merchants: PERMISSIONS.BILLING_FRAUD_CONFIG,
  paymentMethods: PERMISSIONS.BILLING_SETTINGS_MANAGE,
  config: null,
  log: PERMISSIONS.AUDIT_READ,
};

const TAB_KEYS: readonly TabKey[] = [
  'merchants',
  'paymentMethods',
  'config',
  'log',
];

export function WhitelistPage(): JSX.Element {
  const { t } = useTranslation('finance');
  // Hiding the sidebar entry is not access control — the route is reachable
  // by typing the URL. The API is the real boundary; this only avoids
  // rendering a screen whose every request is going to be refused.
  const hasPermission = useHasPermission();
  // Permissions arrive asynchronously. Reading them before they land shows
  // the refusal notice to an operator who DOES have access, which reads as
  // the feature being broken rather than as a slow load.
  const hydrated = useAuthStore((s) => s.hydrated);
  const canOpenPage = hasPermission(PAGE_PERMISSION);
  const allowed = TAB_KEYS.filter((k) => {
    const perm = TAB_PERMISSION[k];
    return perm === null || hasPermission(perm);
  });
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  // A deep link to a tab this operator cannot read falls back to the first
  // one they can, rather than rendering a panel that only 403s.
  const fallback = allowed[0];
  const active: TabKey =
    allowed.includes(raw as TabKey) ? (raw as TabKey) : (fallback ?? 'merchants');

  const selectTab = (tab: TabKey): void => {
    // The default tab clears the param so the canonical URL stays clean.
    setParams(tab === 'merchants' ? {} : { tab }, { replace: true });
  };

  const tabButton = (tab: TabKey): JSX.Element => (
    <button
      key={tab}
      type="button"
      role="tab"
      aria-selected={active === tab}
      data-testid={`whitelist-tab-${tab}`}
      onClick={() => selectTab(tab)}
      className={
        '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ' +
        (active === tab
          ? 'border-pw-primary text-pw-text'
          : 'border-transparent text-pw-text-muted hover:text-pw-text')
      }
    >
      {t(`whitelist.tab.${tab}`)}
    </button>
  );

  // Nothing is decided until permissions land — showing the refusal first
  // and the page a moment later is worse than showing neither.
  if (!hydrated) {
    return (
      <div
        className="flex min-h-[30vh] items-center justify-center"
        role="status"
        aria-busy="true"
        data-testid="whitelist-loading"
      >
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-pw-border border-t-pw-primary" />
      </div>
    );
  }

  if (!canOpenPage || allowed.length === 0) {
    return (
      <div className="p-6" data-testid="whitelist-no-access">
        <p className="rounded border border-pw-border bg-pw-surface px-4 py-10 text-center text-sm text-pw-text-muted">
          {t('whitelist.noAccess')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="whitelist-page">
      <PageHeader
        className="px-6 pt-6"
        title={t('whitelist.title')}
        subtitle={t('whitelist.subtitle')}
      />

      <div
        role="tablist"
        aria-label={t('whitelist.title')}
        className="flex gap-1 border-b border-pw-border px-6"
      >
        {allowed.map((k) => tabButton(k))}
      </div>

      <div className="px-6 pb-6">
        {active === 'merchants' ? (
          <WhitelistMerchantsTab />
        ) : active === 'paymentMethods' ? (
          <WhitelistPaymentMethodsTab />
        ) : active === 'config' ? (
          <WhitelistConfigTab />
        ) : (
          <WhitelistLogTab />
        )}
      </div>
    </div>
  );
}

export default WhitelistPage;
